// apps/web/src/routes/SignUp.tsx
//
// /signup — single form with contact first, then handle (with
// live availability indicator), then profile fields, then inline
// OTP verification. After verify: invalidate AuthContext, navigate
// to /dashboard.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormField } from '../components/ui/FormField.js';
import { Button } from '../components/ui/Button.js';
import { Alert } from '../components/ui/Alert.js';
import { OtpVerifyPanel } from '../components/OtpVerifyPanel.js';
import { apiPost, apiGet, ApiError } from '../lib/apiClient.js';
import { useAuth } from '../lib/auth.js';
import type { OtpSession } from '../lib/api.js';

type Stage = 'form' | 'verify';

const HANDLE_REGEX = /^[a-z][a-z0-9_]{2,29}$/;
const GENDERS = ['male', 'female', 'other', 'prefer_not_to_say'] as const;
const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'IN', name: 'India' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'JP', name: 'Japan' },
  { code: 'BR', name: 'Brazil' },
  { code: 'AU', name: 'Australia' },
  { code: 'MX', name: 'Mexico' },
];

type HandleStatus =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'available' }
  | { kind: 'taken' }
  | { kind: 'invalid'; reason?: string }
  | { kind: 'rate-limited' };

function todayMinus18Years(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return d.toISOString().slice(0, 10);
}

function ageOf(dob: string): number {
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return -1;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age;
}

function normalize(value: string, type: 'email' | 'phone'): string {
  if (type === 'email') return value.toLowerCase().trim();
  const digits = value.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return `+${digits}`;
}

export function SignUp(): JSX.Element {
  const [stage, setStage] = useState<Stage>('form');
  const [contactType, setContactType] = useState<'email' | 'phone'>('email');
  const [contactValue, setContactValue] = useState('');
  const [handle, setHandle] = useState('');
  const [handleStatus, setHandleStatus] = useState<HandleStatus>({ kind: 'idle' });
  const handleCheckSeq = useRef(0);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState<typeof GENDERS[number] | ''>('');
  const [country, setCountry] = useState('US');
  const [acceptedTermsVersion] = useState('2026-08-01');
  const [session, setSession] = useState<{ id: string; method: 'email' | 'sms' } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<{ message: string; requestId: string | null } | null>(null);
  const { invalidate } = useAuth();
  const navigate = useNavigate();

  // Handle availability: debounced 300ms per phase-1-5 §9.7.
  useEffect(() => {
    const seq = ++handleCheckSeq.current;
    if (!handle) {
      setHandleStatus({ kind: 'idle' });
      return;
    }
    if (!HANDLE_REGEX.test(handle)) {
      setHandleStatus({ kind: 'invalid' });
      return;
    }
    setHandleStatus({ kind: 'checking' });
    const t = setTimeout(() => {
      apiGet<{ available: boolean; valid: boolean; reason?: string }>(
        `/api/v1/auth/handle-available?handle=${encodeURIComponent(handle)}`,
      )
        .then((res) => {
          if (seq !== handleCheckSeq.current) return;
          if (!res.data.valid) {
            setHandleStatus({
              kind: 'invalid',
              reason: res.data.reason ?? 'Handle must be 3–30 lowercase letters/digits/underscore, starting with a letter.',
            });
          }
          else if (res.data.available) setHandleStatus({ kind: 'available' });
          else setHandleStatus({ kind: 'taken' });
        })
        .catch((err: unknown) => {
          if (seq !== handleCheckSeq.current) return;
          const apiErr = err as ApiError;
          if (apiErr?.code === 'RATE_LIMITED') setHandleStatus({ kind: 'rate-limited' });
          else setHandleStatus({ kind: 'idle' });
        });
    }, 300);
    return () => clearTimeout(t);
  }, [handle]);

  function validate(): string | null {
    if (!contactValue.trim()) return 'Enter your email or phone.';
    if (!HANDLE_REGEX.test(handle)) {
      return `Handle must be 3–30 lowercase letters, digits, or underscore, starting with a letter. (Got: ${JSON.stringify(handle)})`;
    }
    if (handleStatus.kind === 'taken') return 'This handle is already in use — pick a different one.';
    if (handleStatus.kind === 'invalid') {
      return handleStatus.reason ?? 'Handle is invalid.';
    }
    if (!firstName.trim()) return 'First name is required.';
    if (!displayName.trim()) return 'Display name is required.';
    if (ageOf(dob) < 18) return 'You must be at least 18 years old to create an account.';
    if (!GENDERS.includes(gender as typeof GENDERS[number])) {
      // Optional, no message — default to skipping.
    }
    if (!country) return 'Country is required.';
    if (!acceptedTermsVersion) return 'Terms acceptance is required.';
    return null;
  }

  async function onSubmit(): Promise<void> {
    const msg = validate();
    if (msg) { setError({ message: msg, requestId: null }); return; }
    setError(null);
    setSubmitting(true);
    try {
      const value = normalize(contactValue, contactType);
      const res = await apiPost<OtpSession>('/api/v1/auth/signup', {
        contact: { type: contactType, value },
        handle,
        firstName,
        ...(lastName ? { lastName } : {}),
        displayName,
        dob,
        ...(gender ? { gender } : {}),
        country,
        acceptedTermsVersion,
      });
      // The signup response is generic (same shape whether the
      // insert hit a UNIQUE collision or succeeded). If the backend
      // issued a sessionId, advance to verify; otherwise show
      // "we sent a code" and let the user try (the verify will
      // fail with the same generic error if the contact was
      // already registered — which is correct enumeration-safety).
      if (res.data.sessionId) {
        setSession({ id: res.data.sessionId, method: res.data.method });
        setStage('verify');
      } else {
        setSession({ id: crypto.randomUUID(), method: res.data.method });
        setStage('verify');
      }
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr?.code === 'AGE_REQUIREMENT_NOT_MET') {
        setError({ message: 'You must be at least 18 years old to create an account.', requestId: apiErr.requestId ?? null });
      } else if (apiErr?.code === 'HANDLE_UNAVAILABLE') {
        setError({ message: 'This handle is already in use — pick a different one.', requestId: apiErr.requestId ?? null });
      } else if (apiErr?.code === 'TERMS_REQUIRED') {
        setError({ message: 'Please accept the terms of service.', requestId: apiErr.requestId ?? null });
      } else if (apiErr?.code === 'RATE_LIMITED' && apiErr.retryAfterSec != null) {
        setError({ message: `Too many attempts. Try again in ${apiErr.retryAfterSec}s.`, requestId: apiErr.requestId ?? null });
      } else {
        setError({ message: apiErr?.message ?? 'Could not start signup.', requestId: apiErr.requestId ?? null });
      }
    } finally {
      setSubmitting(false);
    }
  }

  function onSuccess(): void {
    void invalidate();
    navigate('/dashboard', { replace: true });
  }

  function reset(): void {
    setSession(null);
    setError(null);
    setStage('form');
  }

  const handleHelp = (() => {
    switch (handleStatus.kind) {
      case 'idle': return '3–30 lowercase letters, digits, or underscore. Starts with a letter.';
      case 'checking': return 'Checking…';
      case 'available': return 'Available ✓';
      case 'taken': return 'Already in use — pick a different one.';
      case 'invalid': return handleStatus.reason ?? 'Invalid format. 3–30 lowercase letters/digits/underscore.';
      case 'rate-limited': return 'Rate-limited; we\'ll just check on submit.';
    }
  })();

  return (
    <section className="mx-auto max-w-md px-4 py-12">
      <h1 className="text-2xl font-bold">Create account</h1>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        No password. Sign in with a one-time code sent to your email or phone.
      </p>

      {stage === 'form' ? (
        <form
          onSubmit={(e) => { e.preventDefault(); void onSubmit(); }}
          className="mt-6 space-y-4"
        >
          <div>
            <span className="block text-sm font-medium text-gray-800 dark:text-gray-200">
              Contact type
            </span>
            <div className="mt-2 inline-flex rounded border border-gray-300 dark:border-gray-700" role="radiogroup">
              {(['email', 'phone'] as const).map((t) => (
                <label
                  key={t}
                  className={[
                    'cursor-pointer px-3 py-1.5 text-sm',
                    contactType === t
                      ? 'bg-brand-500 text-white'
                      : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800',
                  ].join(' ')}
                >
                  <input
                    type="radio"
                    name="contactType"
                    value={t}
                    checked={contactType === t}
                    onChange={() => setContactType(t)}
                    className="sr-only"
                  />
                  {t === 'email' ? 'Email' : 'Phone'}
                </label>
              ))}
            </div>
          </div>

          <FormField
            label={contactType === 'email' ? 'Email' : 'Phone'}
            type={contactType === 'email' ? 'email' : 'tel'}
            autoComplete={contactType === 'email' ? 'email' : 'tel'}
            value={contactValue}
            onChange={(e) => setContactValue(e.target.value)}
            placeholder={contactType === 'email' ? 'you@example.com' : '+1 555 123 4567'}
            required
          />

          <FormField
            label="@Handle"
            value={handle}
            onChange={(e) => {
              const raw = e.target.value;
              // Strip every Unicode whitespace + format character that
              // might sneak in via paste/IME/autofill. We replace each
              // codepoint if it falls into any of the categories that
              // are non-printable AND not the handle alphabet.
              // The handle alphabet is [a-z0-9_]; everything else is
              // dropped, which gives the cleanest input.
              const cleaned = raw
                .toLowerCase()
                .replace(/[^a-z0-9_]/g, '');
              setHandle(cleaned);
            }}
            placeholder="alice"
            helpText={handleHelp}
            rightAdornment={
              handleStatus.kind === 'available' ? <span aria-hidden="true">✓</span> : null
            }
            required
          />

          <div className="grid grid-cols-2 gap-3">
            <FormField
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              required
            />
            <FormField
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              optional
            />
          </div>

          <FormField
            label="Display name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoComplete="nickname"
            required
            helpText="Shown to other users. Not unique."
          />

          <div className="grid grid-cols-2 gap-3">
            <FormField
              label="Date of birth"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              autoComplete="bday"
              max={todayMinus18Years()}
              required
            />
            <div>
              <label htmlFor="gender" className="block text-sm font-medium text-gray-800 dark:text-gray-200">
                Gender <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">(optional)</span>
              </label>
              <select
                id="gender"
                value={gender}
                onChange={(e) => setGender(e.target.value as typeof GENDERS[number] | '')}
                className="mt-1 block w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
              >
                <option value="">Prefer not to say</option>
                {GENDERS.map((g) => (
                  <option key={g} value={g}>{g.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="country" className="block text-sm font-medium text-gray-800 dark:text-gray-200">
              Country
            </label>
            <select
              id="country"
              value={country}
              onChange={(e) => setCountry(e.target.value.toUpperCase())}
              className="mt-1 block w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              (A small subset; the backend accepts any ISO 3166-1 alpha-2.)
            </p>
          </div>

          <div>
            <label className="flex items-start gap-2 text-sm text-gray-800 dark:text-gray-200">
              <input
                type="checkbox"
                checked
                readOnly
                aria-readonly="true"
                className="mt-1"
              />
              <span>
                I accept the <a href="/terms" className="underline">Terms of Service</a> and{' '}
                <a href="/privacy" className="underline">Privacy Policy</a> (version {acceptedTermsVersion}).
              </span>
            </label>
          </div>

          {error ? <Alert variant="error" requestId={error.requestId}>{error.message}</Alert> : null}

          <Button type="submit" isLoading={submitting}>Create account</Button>
        </form>
      ) : session ? (
        <div className="mt-6">
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            We sent a 6-digit code to your {contactType}.
          </p>
          <OtpVerifyPanel
            mode="signup"
            sessionId={session.id}
            contact={{ type: contactType, value: normalize(contactValue, contactType) }}
            onSuccess={onSuccess}
            onSwitchContact={reset}
            onRestart={reset}
          />
        </div>
      ) : null}
    </section>
  );
}
